import React, { useState } from 'react';
import { Instagram, Facebook, Youtube, Music, CalendarDays, Mail, Phone, Send, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { artistInfo } from '../mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState('');
  const [subMessage, setSubMessage] = useState('');

  const socialLinks = [
    { icon: Instagram, url: artistInfo.socialMedia.instagram, label: 'Instagram' },
    { icon: Facebook, url: artistInfo.socialMedia.facebook, label: 'Facebook' },
    { icon: Youtube, url: artistInfo.socialMedia.youtube, label: 'YouTube' },
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubStatus('sending');
    setSubMessage('');
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setSubStatus('success');
        setSubMessage('You\'re in! A welcome email is on its way to your inbox.');
        setEmail('');
      } else {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail || '';
        if (detail.toLowerCase().includes('already subscribed')) {
          setSubStatus('already');
          setSubMessage('You\'re already subscribed — no action needed!');
        } else {
          setSubStatus('error');
          setSubMessage('Something went wrong. Please try again.');
        }
      }
    } catch {
      setSubStatus('error');
      setSubMessage('Connection error. Please try again.');
    }
  };

  const handleShare = async () => {
    const shareData = { title: 'Brooksgordon', text: 'Check out Brooksgordon — soulful vocalist, pianist & songwriter', url: 'https://brooksgordon.com' };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) { console.error('Share failed:', err.message); }
    } else {
      navigator.clipboard.writeText('https://brooksgordon.com');
    }
  };

  return (
    <footer className="bg-black border-t border-teal-900/20">
      {/* Newsletter Banner */}
      <div className="bg-teal-950/40">
        <div className="container mx-auto px-4 lg:px-8 py-6">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-2 shrink-0">
              <Mail size={16} className="text-gold-400" />
              <span className="text-gold-400 font-semibold text-sm">Stay in the loop</span>
            </div>
            {subStatus !== 'success' ? (
              <form onSubmit={handleSubscribe} className="flex gap-2 flex-1 w-full sm:w-auto max-w-lg">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (subStatus) { setSubStatus(''); setSubMessage(''); } }}
                  placeholder="your@email.com"
                  className="flex-1 bg-black/40 border border-teal-800/40 rounded-full px-4 py-2.5 text-teal-100 placeholder-teal-600 text-sm focus:outline-none focus:border-gold-500/50"
                  data-testid="footer-newsletter-email"
                />
                <button
                  type="submit"
                  disabled={subStatus === 'sending'}
                  className="bg-gold-500 hover:bg-gold-600 text-black px-5 py-2.5 rounded-full font-semibold text-sm transition-all hover:scale-105 flex items-center gap-1.5 whitespace-nowrap disabled:opacity-60"
                  data-testid="footer-newsletter-submit"
                >
                  <Send size={13} />
                  {subStatus === 'sending' ? '...' : 'Subscribe'}
                </button>
              </form>
            ) : (
              <p className="text-green-400 text-sm font-medium">You're in! Check your inbox for a welcome email.</p>
            )}
          </div>
          {subMessage && subStatus !== 'success' && (
            <div className={`max-w-5xl mx-auto mt-3 rounded-lg px-4 py-2 text-sm ${
              subStatus === 'already' ? 'bg-gold-500/10 border border-gold-500/20 text-gold-400' :
              'bg-red-900/20 border border-red-700/30 text-red-300'
            }`} data-testid="newsletter-feedback">
              <p>{subMessage}</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Footer */}
      <div className="container mx-auto px-4 lg:px-8 py-10">
        <div className="max-w-5xl mx-auto">
          {/* Links Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
            {/* Quick Links */}
            <div>
              <h2 className="text-gold-400 text-xs font-semibold uppercase tracking-widest mb-4">Navigate</h2>
              <ul className="space-y-2">
                <li><Link to="/about#bio" className="text-teal-400 hover:text-teal-100 transition-colors text-sm">About</Link></li>
                <li><Link to="/album" className="text-teal-400 hover:text-teal-100 transition-colors text-sm">The Album</Link></li>
                <li><Link to="/music" className="text-teal-400 hover:text-teal-100 transition-colors text-sm">Music</Link></li>
                <li><Link to="/videos" className="text-teal-400 hover:text-teal-100 transition-colors text-sm">Videos</Link></li>
                <li><Link to="/gallery" className="text-teal-400 hover:text-teal-100 transition-colors text-sm">Gallery</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-gold-400 text-xs font-semibold uppercase tracking-widest mb-4">Contact</h2>
              <ul className="space-y-2">
                <li>
                  <Link to="/booking#quick-booking" className="text-teal-400 hover:text-teal-100 transition-colors text-sm flex items-center gap-2">
                    <CalendarDays size={13} className="shrink-0" /> Book an Event
                  </Link>
                </li>
                <li>
                  <a href="mailto:Ishmael.braggs@brooksgordon.com" className="text-teal-400 hover:text-teal-100 transition-colors text-sm flex items-center gap-2 break-all">
                    <Mail size={13} className="shrink-0" /> Ishmael.braggs@brooksgordon.com
                  </a>
                </li>
                <li>
                  <a href="tel:+12623007015" className="text-teal-400 hover:text-teal-100 transition-colors text-sm flex items-center gap-2">
                    <Phone size={13} className="shrink-0" /> +1 (262) 300-7015
                  </a>
                </li>
              </ul>
            </div>

            {/* Listen */}
            <div>
              <h2 className="text-gold-400 text-xs font-semibold uppercase tracking-widest mb-4">Listen</h2>
              <ul className="space-y-2">
                <li>
                  <a href={artistInfo.socialMedia.spotify} target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-100 transition-colors text-sm flex items-center gap-2">
                    <Music size={13} className="shrink-0 text-green-500" /> Spotify
                  </a>
                </li>
                <li>
                  <a href={artistInfo.socialMedia.appleMusic} target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-100 transition-colors text-sm flex items-center gap-2">
                    <Music size={13} className="shrink-0 text-pink-500" /> Apple Music
                  </a>
                </li>
                <li><Link to="/store" className="text-teal-400 hover:text-teal-100 transition-colors text-sm">Store</Link></li>
              </ul>
            </div>

            {/* Social */}
            <div>
              <h2 className="text-gold-400 text-xs font-semibold uppercase tracking-widest mb-4">Follow</h2>
              <div className="flex gap-3">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-teal-900/30 hover:bg-gold-500/20 border border-teal-900/40 hover:border-gold-500/40 rounded-full flex items-center justify-center transition-all hover:scale-110 group"
                      aria-label={social.label}
                    >
                      <Icon size={17} className="text-teal-400 group-hover:text-gold-400 transition-colors" />
                    </a>
                  );
                })}
              </div>
              <button
                onClick={handleShare}
                className="mt-4 flex items-center gap-1.5 text-teal-500 hover:text-gold-400 text-xs uppercase tracking-widest font-semibold transition-colors"
                data-testid="share-site-btn"
              >
                <Share2 size={12} />
                Share This Site
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-teal-900/20 mt-10 pt-8">
            {/* Badges + Company Info — single compact row */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-4">
                <a href="https://copyrightalliance.org/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
                  <img src="https://customer-assets.emergentagent.com/job_gordon-music/artifacts/2bvyrh6b_CM-Badge-2.png" alt="Copyright Alliance" className="h-6 w-6 object-contain" />
                  <span className="text-teal-500 text-[10px] uppercase tracking-wider font-medium">Copyright Alliance</span>
                </a>
                <span className="text-teal-800 hidden sm:inline">|</span>
                <a href="https://www.dnb.com/duns-number.html" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
                  <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_8519c6f9-98ae-4202-bcfa-792b6641688a/artifacts/f7qx5x4g_Screenshot_20260515_013337_Edge.jpg" alt="D-U-N-S Registered" className="h-6 w-6 object-contain rounded" />
                  <span className="text-teal-500 text-[10px] uppercase tracking-wider font-medium">D-U-N-S Registered</span>
                </a>
              </div>

              {/* Company */}
              <div className="flex items-center gap-3">
                <img
                  src="https://customer-assets-m6fa6gv7.emergentagent.net/job_8519c6f9-98ae-4202-bcfa-792b6641688a/artifacts/dehf4d9l_IMG_0240~2.jpg"
                  alt="Brooksgordon Music LC"
                  className="h-8 w-8 object-contain rounded-full"
                />
                <div className="text-right md:text-left">
                  <p className="text-teal-300 text-xs font-semibold">Brooksgordon Music LC</p>
                  <p className="text-teal-600 text-[10px] uppercase tracking-wider">BMI Affiliated</p>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <p className="text-center text-teal-700 text-xs mt-6">
              © {currentYear} Brooksgordon Music LC. All rights reserved. · Brooksgordon Music Publishing (BMI)
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
